import Script from "next/script"
import { useEffect, useState } from "react"
import cn from "classnames"
import Arrow from "public/assets/pawprint-arrow.svg"
import track from "utilities/track"

function Testimonial({ content, attribution, slide, total, visible }) {
  return (
    <blockquote
      className={cn(
        "text-green-pine max-w-4xl text-s3 lg:text-m1 min-w-full bg-white rounded-[30px] shadow-xl py-8 sm:py-16 px-6 sm:px-12 xl:px-32 font-bold h-full self-center transform transition-all duration-500",
        {
          "translate-y-0 opacity-1": visible,
          "translate-y-10 opacity-0": !visible,
        }
      )}
      aria-roledescription="slide"
      role="group"
      aria-label={`${slide} of ${total}`}
    >
      {"“"}
      {content}
      {"”"}
      {attribution ? (
        <footer className="pt-2">
          <cite className="text-gray-dark text-s3 uppercase font-bold not-italic">- {attribution}</cite>
        </footer>
      ) : null}
    </blockquote>
  )
}

export default function Testimonials({ blok }) {
  const lastIndex = blok.testimonials?.length - 1
  const [currentSlide, setSlide] = useState(0)

  const prevSlide = () => {
    setSlide(currentSlide === 0 ? lastIndex : currentSlide - 1)
    track({ event: "click_testimonial_carousel_arrow", direction: "left" })
  }
  const nextSlide = () => {
    setSlide(currentSlide === lastIndex ? 0 : currentSlide + 1)
    track({ event: "click_testimonial_carousel_arrow", direction: "right" })
  }

  const [currentHeight, setCurrentHeight] = useState()

  useEffect(() => {
    setCurrentHeight(
      document.querySelector(`[aria-label="${currentSlide + 1} of ${blok.testimonials?.length}"]`).clientHeight
    )
  }, [blok, currentSlide, currentHeight])

  return (
    <>
      <section
        className="px-8 py-6 sm:py-12 bg-starPrint bg-gray-light md:bg-[length:600px] relative overflow-hidden"
        aria-roledescription="carousel"
        aria-label="Dogtown testimonials"
      >
        <div className="mx-auto max-w-screen-xl relative z-10">
          <h2 className="text-blue text-center sm:pb-8">{blok.title}</h2>
          <div className="max-w-full lg:max-w-3xl xl:max-w-4xl mx-auto py-4">
            <div
              className="flex transition-all duration-700 gap-4 px-2"
              style={{ transform: `translateX(-${currentSlide * 100}%)`, maxHeight: `${currentHeight}px` }}
              aria-live="off"
            >
              {blok.testimonials.map((item, idx) => {
                return (
                  <Testimonial
                    {...item}
                    slide={idx + 1}
                    total={blok.testimonials.length}
                    visible={idx === currentSlide}
                    key={idx}
                  />
                )
              })}
            </div>
          </div>
          {blok.testimonials.length > 1 ? (
            <div className="flex gap-4 sm:pt-4 lg:pt-0 justify-center">
              <button
                onClick={prevSlide}
                className="inline-block lg:absolute left-0 top-1/2"
                aria-label="Previous Slide"
              >
                <Arrow className="carousel-arrow scale-y-75 -scale-x-75 sm:scale-y-100 sm:-scale-x-100 text-blue-dark" />
              </button>
              <button onClick={nextSlide} className="inline-block lg:absolute right-0 top-1/2" aria-label="Next Slide">
                <Arrow className="carousel-arrow scale-75 sm:scale-100 text-blue-dark" />
              </button>
            </div>
          ) : null}
        </div>
      </section>
      <Script type="application/ld+json" id="testimonial-schema">
        {`{
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Dog daycare and boarding",
            "description": "",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5",
              "reviewCount": ${blok.testimonials.length}
            },
            "review": [${blok.testimonials.map(
              (testimonial) => `
              {
                "@type": "Review",
                ${testimonial.attribution ? `"name": "${testimonial.attribution}",` : ""}
                ${
                  testimonial.attribution
                    ? `"author": { "@type": "Person", "name": "${testimonial.attribution}" },`
                    : ""
                }
                "reviewBody": "${testimonial.content}",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5"
                 }
              }`
            )}
          ]
        }`}
      </Script>
    </>
  )
}
